import { useEffect } from "react";
import { useSeo, absAsset } from "@/lib/seo";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Youtube, MapPin, Phone, Mail, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Logo, LangSwitch } from "@/components/common";

export const WHATSAPP_URL = "https://wa.me/905422254467";
export const PHONE_DISPLAY = "+90 542 225 44 67";
export const PHONE_TEL = "+905422254467";
export const ADDRESS = "Terakki Mah. Hüseyin Ekşi Sk. No:9/A Merkez/Zonguldak";
// Map query without "/" so Google Maps shows a pin instead of a route.
export const MAP_QUERY = "Terakki Mah. Hüseyin Ekşi Sk. No:9A, Merkez, Zonguldak, Türkiye";
export const SOCIAL = [
  { name: "Instagram", href: "https://instagram.com/woodesingai", Icon: Instagram },
  { name: "YouTube", href: "https://youtube.com/@woodesingai", Icon: Youtube },
];

export function SocialLinks({ className = "", testid = "social" }) {
  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {SOCIAL.map(({ name, href, Icon }) => (
        <a key={name} href={href} target="_blank" rel="noopener noreferrer" aria-label={name} title={name}
          className="h-9 w-9 grid place-items-center rounded-full border border-[var(--line)] text-neutral-300 hover:text-white hover:border-emerald-600 transition-colors"
          data-testid={`${testid}-${name.toLowerCase()}`}>
          <Icon size={17} />
        </a>
      ))}
    </div>
  );
}

const NAV = [["/", "home"], ["/#features", "features"], ["/#pricing", "pricing"], ["/blog", "blog"], ["/hakkimizda", "about"], ["/iletisim", "contact"]];

function NavItem({ to, k, onClick, className }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const active = !to.includes("#") && (to === "/" ? pathname === "/" : pathname.startsWith(to));
  const cls = `${className} transition-colors ${active ? "text-white" : "text-neutral-300 hover:text-white"}`;
  return <Link to={to} onClick={onClick} className={cls} data-testid={`nav-${k}`}>{t(`nav.${k}`)}</Link>;
}

// Scrolls to top on route change, or to the #section when the URL has a hash (waits for the page to render).
export function ScrollManager() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    let tries = 0;
    const id = setInterval(() => {
      const el = document.getElementById(hash.slice(1));
      if (el || ++tries > 30) {
        clearInterval(id);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 100);
    return () => clearInterval(id);
  }, [pathname, hash]);
  return null;
}

export function SiteHeader() {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#121212]/75 border-b border-[var(--line)]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-6 text-sm">
          {NAV.map(([to, k]) => <NavItem key={k} to={to} k={k} />)}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <LangSwitch className="hidden sm:inline-flex" />
          {user ? (
            <Link to="/app" className="btn-brand rounded-full px-4 py-2 text-sm font-semibold" data-testid="nav-dashboard-btn">{t("nav.dashboard")}</Link>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline text-sm text-neutral-300 hover:text-white" data-testid="nav-login-btn">{t("common.signIn")}</Link>
              <Link to="/register" className="btn-brand rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap" data-testid="nav-register-btn">{t("common.start")}</Link>
            </>
          )}
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden h-9 w-9 grid place-items-center rounded-lg border border-[var(--line)]" aria-label={t("site.menu")} data-testid="mobile-menu-btn"><Menu size={18} /></button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[var(--bg-1)] border-[var(--line)] text-white w-72" data-testid="mobile-menu">
              <SheetTitle className="text-white">{t("site.menu")}</SheetTitle>
              <nav className="mt-6 flex flex-col gap-1">
                {NAV.map(([to, k]) => <NavItem key={k} to={to} k={k} className="py-2.5 text-base" />)}
                {!user && <Link to="/login" className="py-2.5 text-base text-neutral-300 hover:text-white" data-testid="mobile-login-link">{t("common.signIn")}</Link>}
              </nav>
              <LangSwitch className="mt-6" />
              <SocialLinks className="mt-6" testid="mobile-social" />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function FooterCol({ title, children }) {
  return <div><h3 className="font-semibold text-white">{title}</h3><ul className="mt-4 space-y-2.5">{children}</ul></div>;
}

export function SiteFooter() {
  const { t } = useI18n();
  const seo = useSeo();
  const email = t("landing.contactEmail");
  const link = "text-neutral-400 hover:text-white transition-colors";
  return (
    <footer className="border-t border-[var(--line)] bg-[var(--bg-1)]" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-10 text-sm">
        <div>
          <Logo />
          <p className="text-neutral-400 mt-4 max-w-xs leading-relaxed" data-testid="footer-blurb">{t("site.blurb")}</p>
          <SocialLinks className="mt-5" testid="footer-social" />
        </div>
        <FooterCol title={t("site.quickLinks")}>
          {[["/", "home"], ["/hakkimizda", "about"], ["/blog", "blog"], ["/iletisim", "contact"]].map(([to, k]) => (
            <li key={k}><Link to={to} className={link} data-testid={`footer-link-${k}`}>{t(`nav.${k}`)}</Link></li>
          ))}
        </FooterCol>
        <FooterCol title={t("site.usefulLinks")}>
          {["privacy", "terms", "cookies", "subscription"].map((d) => (
            <li key={d}><Link to={`/legal/${d}`} className={link} data-testid={`footer-legal-${d}`}>{t(`legal.${d}`)}</Link></li>
          ))}
          <li><a href={`${process.env.REACT_APP_BACKEND_URL}/api/sitemap.xml`} className={link} data-testid="footer-sitemap">{t("site.sitemap")}</a></li>
        </FooterCol>
        <FooterCol title={t("site.contactInfo")}>
          <li className="flex gap-2.5 text-neutral-400"><MapPin size={16} className="shrink-0 mt-0.5 text-emerald-500" /><span data-testid="footer-address">{ADDRESS}</span></li>
          <li><a href={`tel:${PHONE_TEL}`} className={`${link} flex gap-2.5`} data-testid="footer-phone"><Phone size={16} className="shrink-0 mt-0.5 text-emerald-500" />{PHONE_DISPLAY}</a></li>
          <li><a href={`mailto:${email}`} className={`${link} flex gap-2.5 break-all`} data-testid="contact-email"><Mail size={16} className="shrink-0 mt-0.5 text-emerald-500" />{email}</a></li>
        </FooterCol>
      </div>
      <div className="border-t border-[var(--line)]">
        {seo.payment_logos && (
          <div className="flex justify-center px-5 pt-5" data-testid="footer-payment-logos">
            <img src={absAsset(seo.payment_logos)} alt="Secure payment methods: iyzico, Troy, Visa, Mastercard" loading="lazy"
              className="w-full max-w-[200px] sm:max-w-[280px] h-auto pointer-events-none select-none" draggable={false} />
          </div>
        )}
        <p className="max-w-7xl mx-auto px-5 sm:px-8 py-5 text-xs text-neutral-500 flex flex-wrap gap-x-4 gap-y-1 justify-between">
          <span>© {new Date().getFullYear()} woodesing. {t("site.rights")}</span>
          <span>{t("landing.footerNote")}</span>
        </p>
      </div>
    </footer>
  );
}

export function WhatsAppButton() {
  const { t } = useI18n();
  return (
    <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label={t("site.whatsapp")} title={t("site.whatsapp")}
      className="fixed bottom-5 right-5 z-50 h-14 w-14 rounded-full bg-[#25D366] text-white grid place-items-center shadow-lg shadow-black/40 hover:scale-105 transition-transform"
      data-testid="whatsapp-float-btn">
      <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor" aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    </a>
  );
}

export function PublicPage({ children, testid }) {
  return (
    <div className="min-h-screen flex flex-col" data-testid={testid}>
      <SiteHeader />
      <main className="flex-1">{children}</main>
      <SiteFooter />
    </div>
  );
}
